import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Trash2, Edit2, Loader2, CheckCircle, AlertCircle, X, Save } from 'lucide-react';

const GuidelinesManagement = () => {
  const [activeTab, setActiveTab] = useState('review_criteria');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState(null);
  
  // Data states for each type
  const [reviewCriteria, setReviewCriteria] = useState([]);
  const [itpmCriteria, setItpmCriteria] = useState([]);
  const [subconConstraints, setSubconConstraints] = useState([]);
  const [itpmRisks, setItpmRisks] = useState([]);
  const [goalDescriptions, setGoalDescriptions] = useState([]);
  const [ideaCategories, setIdeaCategories] = useState([]);
  const [thematicClusters, setThematicClusters] = useState([]);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        reviewRes,
        itpmRes,
        subconRes,
        risksRes,
        goalsRes,
        categoriesRes,
        clustersRes
      ] = await Promise.all([
        fetch('/api/guidelines/review_criteria'),
        fetch('/api/guidelines/itpm_criteria'),
        fetch('/api/guidelines/subcon_constraints'),
        fetch('/api/guidelines/itpm_risks'),
        fetch('/api/guidelines/goal_descriptions'),
        fetch('/api/guidelines/idea_categories'),
        fetch('/api/guidelines/thematic_clusters')
      ]);

      const reviewData = await reviewRes.json();
      const itpmData = await itpmRes.json();
      const subconData = await subconRes.json();
      const risksData = await risksRes.json();
      const goalsData = await goalsRes.json();
      const categoriesData = await categoriesRes.json();
      const clustersData = await clustersRes.json();

      setReviewCriteria(reviewData.criteria || []);
      setItpmCriteria(itpmData.criteria || []);
      setSubconConstraints(subconData.constraints || []);
      setItpmRisks(risksData.risks || []);
      setGoalDescriptions(goalsData.goals || []);
      setIdeaCategories(categoriesData.categories || []);
      setThematicClusters(clustersData.clusters || []);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching guidelines data:', err);
      setError('Failed to load guidelines data');
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'review_criteria', label: 'Review Criteria', icon: BookOpen },
    { id: 'itpm_criteria', label: 'ITPM Criteria', icon: BookOpen },
    { id: 'subcon_constraints', label: 'Constraints', icon: BookOpen },
    { id: 'itpm_risks', label: 'Risks', icon: BookOpen },
    { id: 'goal_descriptions', label: 'Goals', icon: BookOpen },
    { id: 'idea_categories', label: 'Categories', icon: BookOpen },
    { id: 'thematic_clusters', label: 'Clusters', icon: BookOpen }
  ];

  const getDataForTab = () => {
    switch (activeTab) {
      case 'review_criteria': return reviewCriteria;
      case 'itpm_criteria': return itpmCriteria;
      case 'subcon_constraints': return subconConstraints;
      case 'itpm_risks': return itpmRisks;
      case 'goal_descriptions': return goalDescriptions;
      case 'idea_categories': return ideaCategories;
      case 'thematic_clusters': return thematicClusters;
      default: return [];
    }
  };

  const getEndpointForTab = () => {
    return `/api/guidelines/${activeTab}`;
  };

  const getFieldsForTab = () => {
    switch (activeTab) {
      case 'review_criteria':
      case 'itpm_criteria':
        return ['criterion_name', 'description', 'display_order'];
      case 'subcon_constraints':
        return ['constraint_type', 'constraint_item', 'display_order'];
      case 'itpm_risks':
        return ['risk_name', 'description', 'likelihood', 'impact', 'display_order'];
      case 'goal_descriptions':
        return ['goal_name', 'description', 'display_order'];
      case 'idea_categories':
        return ['category_name', 'description', 'display_order'];
      case 'thematic_clusters':
        return ['cluster_name', 'description', 'display_order'];
      default:
        return [];
    }
  };

  const handleAdd = () => {
    setFormData({});
    setShowAddModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData(item);
    setShowEditModal(true);
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(getEndpointForTab(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add item');
      }

      await fetchAllData();
      setSuccessMessage('Item added successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowAddModal(false);
      setFormData({});
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${getEndpointForTab()}/${selectedItem.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update item');
      }

      await fetchAllData();
      setSuccessMessage('Item updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowEditModal(false);
      setFormData({});
      setSelectedItem(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${getEndpointForTab()}/${selectedItem.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete item');
      }

      await fetchAllData();
      setSuccessMessage('Item deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowDeleteModal(false);
      setSelectedItem(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFormFields = () => {
    const fields = getFieldsForTab();

    return fields.map(field => {
      const label = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

      if (field === 'likelihood' || field === 'impact') {
        return (
          <div key={field} style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--cares-text-3)' }}>
              {label}
            </label>
            <select
              value={formData[field] || ''}
              onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
              required
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--cares-line-strong)',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">Select {label}</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>
        );
      }

      if (field === 'description') {
        return (
          <div key={field} style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--cares-text-3)' }}>
              {label}
            </label>
            <textarea
              value={formData[field] || ''}
              onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
              required
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--cares-line-strong)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
            />
          </div>
        );
      }

      return (
        <div key={field} style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--cares-text-3)' }}>
            {label}
          </label>
          <input
            type={field === 'display_order' ? 'number' : 'text'}
            value={formData[field] || ''}
            onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
            required={field !== 'display_order'}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid var(--cares-line-strong)',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>
      );
    });
  };

  const renderTableHeaders = () => {
    const fields = getFieldsForTab();
    return fields.filter(f => f !== 'display_order').map(field => {
      const label = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return <th key={field} style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--cares-line-card)' }}>{label}</th>;
    });
  };

  const renderTableRow = (item) => {
    const fields = getFieldsForTab().filter(f => f !== 'display_order');

    return (
      <tr key={item.id} style={{ borderBottom: '1px solid var(--cares-line-card)' }}>
        {fields.map(field => (
          <td key={field} style={{ padding: '12px' }}>
            {field === 'likelihood' || field === 'impact' ? (
              <span style={{
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                background: item[field] === 'High' ? 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))' : item[field] === 'Medium' ? 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))' : 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))',
                color: item[field] === 'High' ? 'var(--cares-orange)' : item[field] === 'Medium' ? 'var(--cares-amber)' : 'var(--cares-green)'
              }}>
                {item[field]}
              </span>
            ) : (
              <span style={{ fontSize: '14px', color: 'var(--cares-text-3)' }}>{item[field]}</span>
            )}
          </td>
        ))}
        <td style={{ padding: '12px', textAlign: 'right' }}>
          <button
            onClick={() => handleEdit(item)}
            style={{
              padding: '6px 12px',
              marginRight: '8px',
              background: 'var(--cares-blue)',
              color: 'var(--cares-text-max)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <Edit2 size={14} />
            Edit
          </button>
          <button
            onClick={() => handleDelete(item)}
            style={{
              padding: '6px 12px',
              background: 'var(--cares-orange)',
              color: 'var(--cares-text-max)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <Trash2 size={14} />
            Delete
          </button>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Loader2 className="animate-spin" size={40} style={{ margin: '0 auto', color: 'var(--cares-purple)' }} />
        <p style={{ marginTop: '16px', color: 'var(--cares-text-label)' }}>Loading guidelines data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', background: 'var(--cares-surface-board)', minHeight: '100vh' }}>

      {/* Success/Error Messages */}
      {successMessage && (
        <div style={{
          padding: '12px 16px',
          background: 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))',
          border: '1px solid var(--cares-green)',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <CheckCircle size={20} style={{ color: 'var(--cares-green)' }} />
          <span style={{ color: 'var(--cares-green)', fontSize: '14px' }}>{successMessage}</span>
        </div>
      )}

      {error && (
        <div style={{
          padding: '12px 16px',
          background: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
          border: '1px solid var(--cares-orange)',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle size={20} style={{ color: 'var(--cares-orange)' }} />
          <span style={{ color: 'var(--cares-orange)', fontSize: '14px' }}>{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid var(--cares-line-card)',
          overflowX: 'auto'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '16px 24px',
                background: activeTab === tab.id ? 'var(--cares-surface-board)' : 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid var(--cares-purple)' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '600' : '400',
                color: activeTab === tab.id ? 'var(--cares-purple)' : 'var(--cares-text-label)',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Add Button */}
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={handleAdd}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                color: 'var(--cares-text-max)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Plus size={16} />
              Add New
            </button>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--cares-surface-board)' }}>
                  {renderTableHeaders()}
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid var(--cares-line-card)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getDataForTab().length === 0 ? (
                  <tr>
                    <td colSpan={getFieldsForTab().length + 1} style={{ padding: '40px', textAlign: 'center', color: 'var(--cares-text-label)' }}>
                      No items found. Click "Add New" to create one.
                    </td>
                  </tr>
                ) : (
                  getDataForTab().map(item => renderTableRow(item))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--cares-text-1)' }}>Add New Item</h2>
              <button
                onClick={() => setShowAddModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cares-text-label)' }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={submitAdd}>
              {renderFormFields()}

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                    color: 'var(--cares-text-max)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    opacity: isSubmitting ? 0.6 : 1
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Add Item
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: 'var(--cares-line-card)',
                    color: 'var(--cares-text-3)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--cares-text-1)' }}>Edit Item</h2>
              <button
                onClick={() => setShowEditModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cares-text-label)' }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={submitEdit}>
              {renderFormFields()}

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                    color: 'var(--cares-text-max)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    opacity: isSubmitting ? 0.6 : 1
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Update Item
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: 'var(--cares-line-card)',
                    color: 'var(--cares-text-3)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--cares-text-1)' }}>Confirm Delete</h2>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cares-text-label)' }}
              >
                <X size={24} />
              </button>
            </div>

            <p style={{ color: 'var(--cares-text-label)', marginBottom: '24px' }}>
              Are you sure you want to delete this item? This action cannot be undone.
            </p>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={confirmDelete}
                disabled={isSubmitting}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'var(--cares-orange)',
                  color: 'var(--cares-text-max)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: isSubmitting ? 0.6 : 1
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete
                  </>
                )}
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isSubmitting}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'var(--cares-line-card)',
                  color: 'var(--cares-text-3)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidelinesManagement;

